// QuickBooks — the QuickBooks tab of Finance › P&L.
//
// Connect the company (admin), see how the last nightly sync went, set what
// each QuickBooks account counts as, and compare the books with CAERA month by
// month. Read only toward QuickBooks: nothing here writes to the books.
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format, startOfMonth, endOfMonth, subMonths, formatDistanceToNow } from 'date-fns';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useRole } from '../RoleGuard';
import { TREATMENTS, summariseBooks, isIncomeSection } from '../../lib/quickbooks';
import { fetchMonthlyPL, fmtMoney } from '../../lib/profitLoss';

const RESULT_MESSAGES = {
  connected:      { tone: 'success', text: 'QuickBooks is connected. The first sync has run; figures are below.' },
  cancelled:      { tone: 'info',    text: 'Connecting was cancelled at Intuit. Nothing was changed.' },
  expired:        { tone: 'danger',  text: 'That connect link had expired or was already used. Click Connect QuickBooks to start again.' },
  invalid:        { tone: 'danger',  text: 'The return from Intuit was not one CAERA started. Click Connect QuickBooks to start again.' },
  not_configured: { tone: 'danger',  text: 'QuickBooks keys are not set on the server yet (QBO_CLIENT_ID / QBO_CLIENT_SECRET).' },
  error:          { tone: 'danger',  text: 'Intuit accepted the sign-in but CAERA could not finish connecting. Try again; if it repeats, check the qbo-callback function logs.' },
};

const SECTION_LABELS = { income: 'Income', other_income: 'Other income', cogs: 'Cost of goods sold', expense: 'Expenses', other_expense: 'Other expenses' };
const SECTION_ORDER = ['income', 'other_income', 'cogs', 'expense', 'other_expense'];

const money0 = (n) => (n < 0 ? '−$' : '$') + Math.abs(Math.round(n || 0)).toLocaleString('en-US');

const Banner = ({ tone, children, onClose }) => (
  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13,
                background: `var(--${tone}-bg)`, color: `var(--${tone})`, border: `0.5px solid var(--${tone}-border)` }}>
    <div style={{ flex: 1 }}>{children}</div>
    {onClose && <button onClick={onClose} aria-label="Dismiss" style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>}
  </div>
);

export default function QuickBooksPanel() {
  const { profile } = useAuth();
  const { isAdmin, isManager } = useRole();
  const [params, setParams] = useSearchParams();
  const [conn, setConn] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [rows, setRows] = useState([]);
  const [caera, setCaera] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  const rangeStart = startOfMonth(subMonths(new Date(), 12));
  const rangeEnd = endOfMonth(new Date());

  const load = async () => {
    setLoading(true); setError('');
    const [c, a, r] = await Promise.all([
      supabase.from('qbo_connections').select('*').maybeSingle(),
      supabase.from('qbo_accounts').select('*').order('full_name'),
      supabase.from('qbo_pl_monthly').select('month, account_id, amount')
        .gte('month', format(rangeStart, 'yyyy-MM-dd')).lte('month', format(rangeEnd, 'yyyy-MM-dd')),
    ]);
    const firstErr = c.error || a.error || r.error;
    if (firstErr) setError(firstErr.message);
    setConn(c.data || null);
    setAccounts(a.data || []);
    setRows(r.data || []);
    try { setCaera(await fetchMonthlyPL(rangeStart, rangeEnd)); } catch (e) { setError(e.message); }
    setLoading(false);
  };

  useEffect(() => {
    const result = params.get('qbo');
    if (result) {
      setNotice(RESULT_MESSAGES[result] || RESULT_MESSAGES.error);
      setParams(prev => { const n = new URLSearchParams(prev); n.delete('qbo'); return n; }, { replace: true });
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const invoke = async (fn, body) => {
    const { data, error: err } = await supabase.functions.invoke(fn, { body });
    if (err) {
      // The function's own message is in the response body.
      let msg = err.message;
      try { const j = await err.context?.json?.(); if (j?.error) msg = j.error; } catch { /* keep generic */ }
      throw new Error(msg);
    }
    if (data?.error) throw new Error(data.error);
    return data;
  };

  const connect = async () => {
    setBusy('connect'); setError('');
    try {
      const { url } = await invoke('qbo-connect', { action: 'start' });
      window.location.assign(url);
    } catch (e) { setError(e.message); setBusy(''); }
  };

  const disconnect = async () => {
    setBusy('disconnect'); setError('');
    try {
      await invoke('qbo-connect', { action: 'disconnect' });
      setConfirmDisconnect(false);
      setNotice({ tone: 'info', text: 'QuickBooks is disconnected. Figures already synced stay, and the P&L keeps using them for those months.' });
      await load();
    } catch (e) { setError(e.message); }
    setBusy('');
  };

  const syncNow = async () => {
    setBusy('sync'); setError('');
    try {
      const r = await invoke('qbo-sync', { trigger: 'manual' });
      setNotice({ tone: 'success', text: `Synced ${r.accounts} accounts, ${r.range}.` });
    } catch (e) { setError(e.message); }
    await load();
    setBusy('');
  };

  const setTreatment = async (acct, treatment) => {
    setError('');
    const patch = { treatment, treatment_set_by: profile?.id || null, treatment_set_by_name: profile?.full_name || null, treatment_set_at: new Date().toISOString() };
    const { error: err } = await supabase.from('qbo_accounts').update(patch).eq('id', acct.id);
    if (err) { setError(err.message); return; }
    setAccounts(prev => prev.map(a => a.id === acct.id ? { ...a, ...patch } : a));
    try { setCaera(await fetchMonthlyPL(rangeStart, rangeEnd)); } catch { /* table below still reflects the change */ }
  };

  const books = useMemo(() => summariseBooks(rows, accounts), [rows, accounts]);
  const totalsByAccount = useMemo(() => {
    const t = {};
    for (const r of rows) t[r.account_id] = (t[r.account_id] || 0) + (Number(r.amount) || 0);
    return t;
  }, [rows]);

  const th = { padding: '9px 14px', fontSize: 11, color: 'var(--text3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'right', whiteSpace: 'nowrap' };
  const td = { padding: '9px 14px', fontSize: 12.5, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
  const muted = { fontSize: 12, color: 'var(--text3)' };

  if (loading && !conn && !accounts.length) {
    return <div className="empty-state"><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>;
  }

  const diff = (a, b) => {
    const d = (a || 0) - (b || 0);
    if (Math.abs(d) < 1) return null;
    return <div style={{ fontSize: 11, color: 'var(--text3)' }}>{d > 0 ? '+' : '−'}{money0(Math.abs(d))} vs CAERA</div>;
  };

  return (
    <div>
      {notice && <Banner tone={notice.tone} onClose={() => setNotice(null)}>{notice.text}</Banner>}
      {error && <Banner tone="danger" onClose={() => setError('')}>{error}</Banner>}

      {/* Connection */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="card-header">
          <div className="card-title">QuickBooks Online</div>
          {conn && (
            <span className={`badge ${conn.last_sync_status === 'error' ? 'badge-urgent' : 'badge-done'}`}>
              {conn.last_sync_status === 'error' ? 'Sync failed' : 'Connected'}
            </span>
          )}
        </div>
        <div style={{ padding: '14px 16px', display: 'grid', gap: 10 }}>
          {!conn ? (
            <>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text2)', maxWidth: 680 }}>
                Connect the company's QuickBooks Online so the P&amp;L takes overhead from the books instead of the Overhead tab.
                CAERA only reads from QuickBooks. It syncs the monthly profit and loss every night and never creates or changes anything in the books.
              </p>
              <div>
                {isAdmin
                  ? <button className="btn btn-primary btn-sm" onClick={connect} disabled={busy === 'connect'}>{busy === 'connect' ? 'Opening Intuit…' : 'Connect QuickBooks'}</button>
                  : <span style={muted}>An admin connects QuickBooks.</span>}
              </div>
            </>
          ) : (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 24px', fontSize: 13 }}>
                <span><span style={muted}>Company </span>{conn.company_name || `Realm ${conn.realm_id}`}{conn.environment === 'sandbox' && <span className="badge badge-low" style={{ marginLeft: 6 }}>Sandbox</span>}</span>
                <span><span style={muted}>Connected </span>{format(new Date(conn.connected_at), 'd MMM yyyy')}{conn.connected_by_name ? ` by ${conn.connected_by_name}` : ''}</span>
                <span><span style={muted}>Last sync </span>{conn.last_sync_at ? `${formatDistanceToNow(new Date(conn.last_sync_at))} ago` : 'not yet'}</span>
                {conn.last_sync_range && <span><span style={muted}>Range </span>{conn.last_sync_range}</span>}
              </div>
              {conn.last_sync_status === 'error' && conn.last_sync_error && (
                <div style={{ fontSize: 12.5, color: 'var(--danger)' }}>{conn.last_sync_error}</div>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {isManager && <button className="btn btn-primary btn-sm" onClick={syncNow} disabled={!!busy}>{busy === 'sync' ? 'Syncing…' : 'Sync now'}</button>}
                {isAdmin && /reconnect/i.test(conn.last_sync_error || '') && (
                  <button className="btn btn-outline btn-sm" onClick={connect} disabled={!!busy}>Reconnect</button>
                )}
                {isAdmin && !confirmDisconnect && <button className="btn btn-outline btn-sm" onClick={() => setConfirmDisconnect(true)} disabled={!!busy}>Disconnect</button>}
                {isAdmin && confirmDisconnect && (
                  <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', fontSize: 12.5 }}>
                    Disconnect QuickBooks? Synced figures stay.
                    <button className="btn btn-outline btn-sm" style={{ color: 'var(--danger)', borderColor: 'var(--danger-border)' }} onClick={disconnect} disabled={!!busy}>{busy === 'disconnect' ? 'Disconnecting…' : 'Disconnect'}</button>
                    <button className="btn btn-outline btn-sm" onClick={() => setConfirmDisconnect(false)}>Keep</button>
                  </span>
                )}
                <span style={{ ...muted, marginLeft: 'auto' }}>Syncs nightly at 3 am ET.</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Books vs CAERA */}
      {rows.length > 0 && caera && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-header">
            <div className="card-title">Books and CAERA, by month</div>
            <span style={muted}>Last 13 months</span>
          </div>
          <div className="table-wrapper">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={{ ...th, textAlign: 'left' }}>Month</th>
                <th style={th}>Revenue (books)</th>
                <th style={th}>Payroll (books)</th>
                <th style={th}>Overhead (books)</th>
                <th style={th}>Net income (books)</th>
                <th style={th}>Net (CAERA, cash)</th>
              </tr></thead>
              <tbody>
                {[...caera.months].reverse().map(m => {
                  const b = books[m.key];
                  return (
                    <tr key={m.key} style={{ borderTop: '0.5px solid var(--border)', verticalAlign: 'top' }}>
                      <td style={{ ...td, textAlign: 'left', fontWeight: 500 }}>{m.label}</td>
                      {b ? (
                        <>
                          <td style={td}>{fmtMoney(b.revenue)}{diff(b.revenue, m.revCash)}</td>
                          <td style={td}>{fmtMoney(b.payroll)}{diff(b.payroll, m.payroll)}</td>
                          <td style={td}>{fmtMoney(b.overhead)}</td>
                          <td style={{ ...td, fontWeight: 600, color: b.net < 0 ? 'var(--danger)' : 'var(--text)' }}>{fmtMoney(b.net)}</td>
                        </>
                      ) : <td colSpan={4} style={{ ...td, textAlign: 'center', color: 'var(--text3)' }}>Nothing in the books for this month</td>}
                      <td style={{ ...td, color: m.netCash < 0 ? 'var(--danger)' : 'var(--text2)' }}>{fmtMoney(m.netCash)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '8px 14px', borderTop: '0.5px solid var(--border)', fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 }}>
            "vs CAERA" compares the books with payments recorded against claim lines (revenue) and with payroll runs (payroll).
            A large gap usually means remittances or payroll weeks are missing in CAERA. Net income (books) counts every account, including ones left out of the CAERA P&amp;L.
          </div>
        </div>
      )}

      {/* Accounts */}
      {accounts.length > 0 && (
        <div className="card">
          <div className="card-header">
            <div className="card-title">What each account counts as</div>
            <span style={muted}>Only Overhead flows into the CAERA P&amp;L</span>
          </div>
          <div className="table-wrapper">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={{ ...th, textAlign: 'left' }}>Account</th>
                <th style={th}>Last 13 months</th>
                <th style={{ ...th, textAlign: 'left' }}>Counts as</th>
              </tr></thead>
              <tbody>
                {SECTION_ORDER.flatMap(sec => {
                  const list = accounts.filter(a => a.section === sec);
                  if (!list.length) return [];
                  return [
                    <tr key={`h-${sec}`} style={{ background: 'var(--bg2)' }}>
                      <td colSpan={3} style={{ ...td, textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text3)' }}>{SECTION_LABELS[sec]}</td>
                    </tr>,
                    ...list.map(a => (
                      <tr key={a.id} style={{ borderTop: '0.5px solid var(--border)' }}>
                        <td style={{ ...td, textAlign: 'left', whiteSpace: 'normal' }}>
                          {a.full_name.includes(':') && <span style={{ color: 'var(--text3)' }}>{a.full_name.split(':').slice(0, -1).join(' › ')} › </span>}
                          {a.name}
                        </td>
                        <td style={td}>{fmtMoney(totalsByAccount[a.account_id] || 0)}</td>
                        <td style={{ ...td, textAlign: 'left' }}>
                          {isManager ? (
                            <select className="form-input" style={{ width: 150, padding: '4px 8px', fontSize: 12.5 }} value={a.treatment}
                              aria-label={`What ${a.name} counts as`}
                              onChange={e => setTreatment(a, e.target.value)}>
                              {TREATMENTS.filter(t => isIncomeSection(a.section) ? t.key !== 'payroll' : t.key !== 'revenue')
                                .map(t => <option key={t.key} value={t.key} title={t.desc}>{t.label}</option>)}
                            </select>
                          ) : TREATMENTS.find(t => t.key === a.treatment)?.label}
                          <span style={{ ...muted, fontSize: 11, marginLeft: 8 }}>
                            {a.treatment_set_by_name ? `set by ${a.treatment_set_by_name}` : 'default'}
                          </span>
                        </td>
                      </tr>
                    )),
                  ];
                })}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '8px 14px', borderTop: '0.5px solid var(--border)', fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 }}>
            {TREATMENTS.map(t => <div key={t.key}><strong style={{ color: 'var(--text2)' }}>{t.label}</strong>: {t.desc}</div>)}
          </div>
        </div>
      )}
    </div>
  );
}
